import { T, useT } from '@transifex/react';
import { VStack } from '@vidahealth/vida-ui';

import { HoldTightScreen } from '@/components/HoldTightScreen';
import { useRulesEngine } from '@/modules/rulesEngine';
import {
  ContactSupport,
  CtaButton,
  Screen,
  ScreenHeading,
  TextField,
  formatZipCode,
} from '@/modules/ui';

/**
 * Eligibility step for orgs that verify via ZIP code alone (e.g. CVS
 * Caremark's `FN_LN_DOB_AND_EMPID_OR_ZC` — the ZC branch). Mirrors stylos's
 * EmployeeZipVerificationPage structurally, with hardcoded copy.
 */
export function EmployeeZipVerificationScreen() {
  const t = useT();
  const {
    draft,
    setDraft,
    runEligibilityCheck,
    eligibilityCheckResult,
    setEligibilityCheckResult,
  } = useRulesEngine();

  const onChangeText = (text: string) => {
    setDraft((prev) => ({ ...prev, zipCode: text }));

    // Clear any prior submit result so a previous "couldn't verify" error
    // doesn't hang around once the user starts editing again.
    setEligibilityCheckResult(undefined);
  };

  // Wrap rather than passing runEligibilityCheck straight to onPress — its
  // optional `{ isRetry }` arg would otherwise receive the press event.
  const onContinue = () => {
    void runEligibilityCheck();
  };

  const zipCode = draft.zipCode ?? '';
  const isChecking = eligibilityCheckResult?.isChecking === true;
  const isContinueDisabled = !zipCode.trim() || isChecking;

  // This screen only renders for the ZIP (RTE) field-set, so any in-flight
  // check here is a real-time check. Hold Tight stays on this route rather than
  // becoming its own onboarding step.
  if (isChecking) {
    return <HoldTightScreen />;
  }

  return (
    <Screen
      footer={
        <VStack space='lg'>
          <ContactSupport showPhone />
          <CtaButton
            isBusy={isChecking}
            isDisabled={isContinueDisabled}
            onPress={onContinue}
            testID='employee-zip-verification-continue'
          >
            <T _str='Continue' />
          </CtaButton>
        </VStack>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={t('Your organization requires additional information to verify your identity')}
        />

        <TextField
          value={zipCode}
          onChangeText={onChangeText}
          format={formatZipCode}
          placeholder={t('ZIP Code')}
          autoComplete='postal-code'
          inputMode='numeric'
          keyboardType='number-pad'
          maxLength={5}
          testID='employee-zip-verification-zip'
        />
      </VStack>
    </Screen>
  );
}
